import { useMutation, useQuery } from '@tanstack/react-query';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import firebaseApp from '../firebase-app';
import {
  deleteNotification,
  getScheduledNotification,
  scheduleNewsNotification,
  updateScheduledNewsNotification,
} from '../api/push-notification';
import { SchedulePushdNotificationBody, PushNotificationConnection } from '../types/notification';
import moment from 'moment';
import { useUserAuth } from '../contexts/auth';
import { FS_PATH_PUSH_NOTIFICATION_CONTENT_CONNECTION } from '../constants';

const getPath = (id: string, abbrv: string) =>
  `${FS_PATH_PUSH_NOTIFICATION_CONTENT_CONNECTION}/${abbrv}/NEWS/${id}`;
const getNewsItemConnection = async (params: {
  id: string;
  abbrv: string;
}): Promise<PushNotificationConnection | null> => {
  const { id, abbrv } = params;
  const docs = await getDoc(doc(firebaseApp.db, getPath(id, abbrv)));
  if (docs.exists()) {
    return docs?.data() as PushNotificationConnection;
  }

  return null;
};
export function useNewsNotificationConf(params: { abbrv: string; id: string }) {
  const { abbrv, id } = params;
  const { currentUser } = useUserAuth();

  const query = useQuery({
    queryKey: ['news', 'notification', abbrv, id],
    queryFn: async (): Promise<SchedulePushdNotificationBody | null> => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }
      const newsItemConnection = await getNewsItemConnection({
        abbrv,
        id,
      });
      // We save the notification id as array for the future in case we want to support multiple notifications.
      // The id has two parts entity-id and current date
      const pushNotificationId = newsItemConnection?.pushNotificationIds?.[0];
      if (!pushNotificationId) {
        return null;
      }

      const newsNotification = await getScheduledNotification(abbrv, pushNotificationId, auth);

      return newsNotification || null;
    },
    refetchOnWindowFocus: false,
  });
  const createMutation = useMutation({
    mutationFn: async (payload: Parameters<typeof scheduleNewsNotification>[0]['body']) => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }
      const notificationRes = await scheduleNewsNotification({
        abbrv,
        body: {
          notification: payload.notification,
          id: `${id}-${moment().valueOf()}`,
          scheduledTime: payload.scheduledTime,
        },
        auth,
      });
      if (notificationRes.id) {
        const path = getPath(id, abbrv);
        const docRef = doc(firebaseApp.db, path);
        await setDoc(docRef, {
          pushNotificationIds: [notificationRes.id],
        });
      }

      return notificationRes;
    },
    onSuccess() {
      query.refetch();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: Parameters<typeof updateScheduledNewsNotification>[0]['body']) => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }
      const notificationRes = await updateScheduledNewsNotification({
        abbrv,
        body: {
          notification: payload.notification,
          id: payload.id,
          scheduledTime: payload.scheduledTime,
          createdAt: payload.createdAt,
        },
        auth,
      });
      return notificationRes;
    },
    onSuccess() {
      query.refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (params: { id: string; newsId: string }) => {
      const { id, newsId } = params;
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }
      await deleteNotification(id, auth);

      const path = getPath(newsId, abbrv);
      const docRef = doc(firebaseApp.db, path);
      const res = await updateDoc(docRef, {
        pushNotificationIds: null,
      });
      return res;
    },
    onSuccess() {
      query.refetch();
    },
  });
  return {
    query,
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
